import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { ProviderOnboardingStep } from "@/features/onboarding/enums/ProviderOnboardingStep";
import { ProviderUser } from "@/common/types/ProviderUser";
import { Button } from "@/components/ui/button";
import { NativeCredentialUploadStep } from "./native-credential-upload-step";
import { OnboardingDocumentChecklist } from "./onboarding-document-checklist";
import { CredentialUploadReminderForm } from "./credential-upload-reminder-form";
import { ArrowLeft } from "lucide-react";
import { ONBOARDING_FORM_STYLES } from "@/features/onboarding/components/onboarding-form-styles";
import { OnboardingStepHeader } from "@/features/onboarding/components/onboarding-step-header";

const CredentialUploadStep = ({
  onComplete,
  onBack,
  providerUser,
}: {
  onComplete: () => void;
  onBack?: () => void;
  providerUser: ProviderUser;
}) => {
  const trpc = useTRPC();
  const { mutateAsync: updateOnboardingStep } = useMutation(
    trpc.providerOnboarding.updateProviderOnboardingStep.mutationOptions(),
  );

  const [hasDocuments, setHasDocuments] = useState<boolean | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleComplete = async () => {
    await updateOnboardingStep({
      onboardingStepId: ProviderOnboardingStep.UPLOAD_DOCUMENTS,
    });
    onComplete();
  };

  if (hasDocuments === null) {
    return (
      <div>
        {onBack && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className={ONBOARDING_FORM_STYLES.backButton}
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back
          </Button>
        )}
        <OnboardingStepHeader
          title="Credentialing Documents"
          description="Add your documents to keep your credentials organized and ready for your next opportunity."
        />
        <OnboardingDocumentChecklist />
        <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
          <h3 className={ONBOARDING_FORM_STYLES.sectionHeading}>
            Do you have your credentialing documents on this device?
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Upload them now, or choose a reminder for later.
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Button
              onClick={() => setHasDocuments(true)}
              className={`${ONBOARDING_FORM_STYLES.primaryButton} sm:w-full`}
            >
              Yes
            </Button>
            <Button
              variant="outline"
              onClick={() => setHasDocuments(false)}
              className={`${ONBOARDING_FORM_STYLES.secondaryButton} sm:w-full`}
            >
              No
            </Button>
          </div>
        </section>
      </div>
    );
  }

  if (hasDocuments === false) {
    return <CredentialUploadReminderForm onSubmit={handleComplete} />;
  }

  return (
    <NativeCredentialUploadStep
      userId={providerUser.id}
      onComplete={handleComplete}
      onBack={onBack}
    />
  );
};

export default CredentialUploadStep;
